import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Alert, Button, Form } from "antd";
import AppModal from "../../common/components/AppModal";
import FollowUpTaskCreationStore, {
    FollowUpTaskValues,
    followUpDescription,
} from "../stores/FollowUpTaskCreationStore";
import FollowUpTaskService from "../services/FollowUpTaskService";
import WorkbenchFollowUpTaskForm from "./WorkbenchFollowUpTaskForm";

// One follow-up action per subject page (React 17 has no useId).
const unavailableId = "workbench-follow-up-unavailable";

const WorkbenchFollowUpTask: React.FC<{
    subjectId: string;
    onCreated: () => Promise<void>;
}> = ({ subjectId, onCreated }) => {
    const [form] = Form.useForm<FollowUpTaskValues>();
    const [creationStore] = useState(
        () => new FollowUpTaskCreationStore(new FollowUpTaskService()),
    );
    useEffect(() => {
        void creationStore.initialize(subjectId);
        return creationStore.dispose;
    }, [creationStore, subjectId]);
    const {
        options,
        open,
        saving,
        recovering,
        error,
        unavailable,
        actionDisabled,
    } = creationStore;
    const start = () =>
        creationStore.start((options) => {
            form.resetFields();
            form.setFieldsValue({
                projectId: options.suggestedProjectId ?? undefined,
                description: followUpDescription(options),
            });
        });
    const save = () =>
        creationStore.save(() => form.validateFields(), onCreated);
    const projectId = Form.useWatch("projectId", form);
    const title = Form.useWatch("title", form);
    const taskType = Form.useWatch("taskType", form);
    const project = options?.projects.find((x) => x.id === projectId);
    const requiresType = !!project && !project.defaultTaskTypeId;

    return (
        <>
            <Button
                className="workbench-new-subject-action-row workbench-new-subject-action-row-primary"
                disabled={actionDisabled}
                onClick={start}
                type="link"
                aria-describedby={unavailable ? unavailableId : undefined}
                icon={
                    <span className="workbench-new-subject-action-glyph workbench-new-subject-action-glyph-task" />
                }
            >
                <span className="workbench-new-subject-action-label">
                    Create follow-up task
                </span>
            </Button>
            {/* Visible (not tooltip-only) so the reason is available while the button is disabled. */}
            {unavailable && (
                <div
                    id={unavailableId}
                    className="workbench-follow-up-unavailable"
                >
                    {unavailable}
                </div>
            )}
            <AppModal
                open={open}
                title="Create follow-up task"
                width={620}
                destroyOnClose
                onCancel={creationStore.cancel}
                closable={!saving}
                maskClosable={false}
                cancelButtonTitle="Cancel"
                cancelButtonProps={{ disabled: saving }}
                onCancelClick={creationStore.cancel}
                submitButtonTitle={recovering ? "Retry save" : "Create task"}
                submitButtonProps={{
                    htmlType: "button",
                    loading: saving,
                    disabled:
                        !recovering &&
                        (!projectId ||
                            !title?.trim() ||
                            !project?.taskTypes.length ||
                            (requiresType && !taskType)),
                }}
                onSubmitClick={() => void save().catch(() => undefined)}
                data-id-name="workbench-follow-up-task"
            >
                {options && (
                    <>
                        <div className="workbench-follow-up-context">
                            <div>Linked to this case</div>
                            <strong>{options.caseLabel}</strong>
                            <div>
                                Subject: {options.subjectKind}
                                {options.submissionId &&
                                    ` · Submission ID: ${options.submissionId}`}
                            </div>
                        </div>
                        {error && (
                            <Alert type="error" showIcon message={error} />
                        )}
                        {recovering && (
                            <Alert
                                type="info"
                                message="Retry the submitted values to recover the save result without creating another task."
                                description="If retrying keeps failing, discard this save. Check the case's Tasks tab first: the task may already exist."
                                action={
                                    <Button
                                        size="small"
                                        disabled={saving}
                                        onClick={creationStore.discard}
                                    >
                                        Discard save
                                    </Button>
                                }
                            />
                        )}
                        <WorkbenchFollowUpTaskForm
                            form={form}
                            options={options}
                            project={project}
                            requiresType={requiresType}
                            creationStore={creationStore}
                        />
                    </>
                )}
            </AppModal>
        </>
    );
};

export default observer(WorkbenchFollowUpTask);
